import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GetCardSignaturesResponse,
  ListCardsResponseWithProjectName,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { NonRetryableError } from "../errors/nonRetryableError.ts";
import { queryKeys } from "./queryKeys.ts";

export const useGetCardSignatures = (cardId: string, projectId: string) => {
  const { getToken, isAuthenticated } = useAuthentication();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.cardSignatures(projectId, cardId),
    queryFn: async ({ signal }) => {
      const token = await getToken();
      const response = await fetch(
        `/api/projects/${projectId}/cards/${cardId}/signatures`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        },
      );

      if (response.ok) {
        return (await response.json()) as GetCardSignaturesResponse;
      }

      if (response.status === 403) {
        throw new NonRetryableError("No permission to load the signatures");
      }

      throw new Error(`Loading the signatures failed: ${response.status}`);
    },
    retry: (failureCount, error) =>
      !(error instanceof NonRetryableError) && failureCount < 3,
    enabled: isAuthenticated && !!cardId,
    // Read-your-writes: don't refetch from the lagging view on drawer reopen
    staleTime: 10_000,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    placeholderData: (_): GetCardSignaturesResponse | undefined => {
      const cards = queryClient.getQueryData([
        "cards",
        "list",
        projectId,
      ]) as ListCardsResponseWithProjectName;

      const card = cards?.items.find((item) => item.id === cardId);
      return (
        card?.signatures && {
          firstName: card.firstName,
          lastName: card.lastName,
          signatures: card.signatures,
        }
      );
    },
  });
};
